import { Injectable } from '@angular/core';
import { Observable, map, switchMap } from 'rxjs';

import {
  REPORT_QUERY_GET_SOURCE_ENDPOINT,
  REPORT_QUERY_GET_SOURCES_ENDPOINT,
  REPORT_QUERY_POST_EXECUTE_ENDPOINT,
  REPORT_QUERY_POST_VERIFY_ENDPOINT,
  REPORT_QUERY_POST_VERIFY_OUTPUT_ENDPOINT,
} from '../api/api-endpoints';
import { RestApiService } from '../api/rest-api.service';

export type ReportDataType =
  | 'string'
  | 'uuid'
  | 'integer'
  | 'number'
  | 'boolean'
  | 'date'
  | 'datetime'
  | 'currency'
  | 'percentage';

export type ReportOutputKind = 'number' | 'percentage' | 'time_series' | 'category_series' | 'pie' | 'table' | 'spreadsheet';

export type ReportVisualization =
  'number' | 'currency' | 'percentage_bar' | 'percentage_ring' | 'line' | 'area' | 'bar' | 'pie' | 'donut' | 'treemap' | 'table' | 'spreadsheet';

export interface ReportQuery {
  version: 1;
  source: string;
  from?: string;
  duration?: number;
  select: Array<Record<string, unknown>>;
  joins?: Array<{ join: string }>;
  where?: Record<string, unknown>;
  group_by?: Array<{
    field: string;
    bucket?: 'day' | 'week' | 'month' | 'quarter' | 'year';
  }>;
  having?: Record<string, unknown>;
  order_by?: Array<{ ref: string; direction: 'asc' | 'desc' }>;
  limit?: number;
  offset?: number;
  include_total?: boolean;
}

export interface ReportResultColumn {
  key: string;
  label: string;
  role: 'dimension' | 'measure';
  data_type: ReportDataType;
}

export interface ReportResult {
  source: string;
  columns: ReportResultColumn[];
  rows: Array<Record<string, unknown>>;
  meta: { row_count: number; total_count?: number };
}

export interface ReportOutputBindings {
  value?: string;
  category?: string;
  series_by?: string;
  series: string[];
}

export interface ReportOutputVerification {
  valid: true;
  output: ReportOutputKind;
  columns: ReportResultColumn[];
  bindings: ReportOutputBindings;
  coercions: Array<{
    key: string;
    from: ReportDataType;
    to: 'string';
  }>;
  time_window: {
    field: string;
    from: string;
    until: string;
    duration: number;
  };
}

export interface ReportWidgetData {
  verification: ReportOutputVerification;
  result: ReportResult;
}

export interface ReportSourceSummary {
  id: string;
  label: string;
}

export interface PublicReportSourceSchema extends ReportSourceSummary {
  time_field: string;
  columns: Array<Record<string, unknown>>;
  joins: Array<Record<string, unknown>>;
}

@Injectable({ providedIn: 'root' })
export class ReportQueryService {
  constructor(private readonly restApi: RestApiService) {}

  getSources(): Observable<ReportSourceSummary[]> {
    return this.restApi.get<ReportSourceSummary[]>(REPORT_QUERY_GET_SOURCES_ENDPOINT);
  }

  getSource(sourceId: string): Observable<PublicReportSourceSchema> {
    const endpoint = REPORT_QUERY_GET_SOURCE_ENDPOINT.replace('{0}', encodeURIComponent(sourceId));
    return this.restApi.get<PublicReportSourceSchema>(endpoint);
  }

  verify(query: ReportQuery): Observable<{ valid: true }> {
    return this.restApi.post<{ valid: true }>(REPORT_QUERY_POST_VERIFY_ENDPOINT, query);
  }

  verifyOutput(query: ReportQuery, output: ReportOutputKind): Observable<ReportOutputVerification> {
    return this.restApi.post<ReportOutputVerification>(REPORT_QUERY_POST_VERIFY_OUTPUT_ENDPOINT, {
      query,
      output,
    });
  }

  execute(query: ReportQuery): Observable<ReportResult> {
    return this.restApi.post<ReportResult>(REPORT_QUERY_POST_EXECUTE_ENDPOINT, query);
  }

  loadWidget(query: ReportQuery, output: ReportOutputKind): Observable<ReportWidgetData> {
    return this.verifyOutput(query, output).pipe(
      switchMap((verification) =>
        this.execute(query).pipe(map((result) => ({ verification, result }))),
      ),
    );
  }

  withTimeWindow(query: ReportQuery, from: string, duration: number): ReportQuery {
    return { ...query, from, duration };
  }
}
